import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  User as FirebaseUser,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  applyActionCode
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { AppCheck, getToken, initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { User } from '../types';

const firebaseConfig = {
  apiKey: (process.env.REACT_APP_FIREBASE_API_KEY || '').trim(),
  authDomain: (process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '').trim(),
  projectId: (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim(),
  storageBucket: (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim(),
  messagingSenderId: (process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '').trim(),
  appId: (process.env.REACT_APP_FIREBASE_APP_ID || '').trim(),
  measurementId: (process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '').trim() || undefined,
};

const requiredFirebaseEnvVars = [
  ['REACT_APP_FIREBASE_API_KEY', firebaseConfig.apiKey],
  ['REACT_APP_FIREBASE_AUTH_DOMAIN', firebaseConfig.authDomain],
  ['REACT_APP_FIREBASE_PROJECT_ID', firebaseConfig.projectId],
  ['REACT_APP_FIREBASE_STORAGE_BUCKET', firebaseConfig.storageBucket],
  ['REACT_APP_FIREBASE_MESSAGING_SENDER_ID', firebaseConfig.messagingSenderId],
  ['REACT_APP_FIREBASE_APP_ID', firebaseConfig.appId],
];

requiredFirebaseEnvVars.forEach(([name, value]) => {
  if (!value) {
    throw new Error(`Missing Firebase web config: ${name}`);
  }
});

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();
let lastKnownIdToken: string | null = null;
const appCheckEnabled = process.env.REACT_APP_ENABLE_APPCHECK === 'true';
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY || '';
const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || '';

let appCheck: AppCheck | null = null;
if (appCheckEnabled && appCheckSiteKey) {
  if (typeof window !== 'undefined' && appCheckDebugToken) {
    const debugTokenValue = appCheckDebugToken === 'true' ? true : appCheckDebugToken;
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugTokenValue;
  }
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

googleProvider.setCustomParameters({
  prompt: 'select_account'
});

void setPersistence(auth, browserLocalPersistence);

class FirebaseService {
  auth = auth;
  storage = storage;
  appCheck = appCheck;

  getCurrentUser(): Promise<User | null> {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          resolve(this.mapFirebaseUserToUser(firebaseUser));
        } else {
          resolve(null);
        }
      });
    });
  }

  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      lastKnownIdToken = await user.getIdToken();
      return lastKnownIdToken;
    }
    return lastKnownIdToken;
  }

  async getAppCheckToken(): Promise<string | null> {
    if (!appCheck) return null;
    try {
      const tokenResult = await getToken(appCheck, false);
      return tokenResult?.token || null;
    } catch (error) {
      return null;
    }
  }

  async signInWithEmailPassword(email: string, password: string): Promise<User> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      lastKnownIdToken = await result.user.getIdToken(true);
      return this.mapFirebaseUserToUser(result.user);
    } catch (error: any) {
      throw this.buildFirebaseError(error);
    }
  }

  async createUserWithEmailPassword(
    email: string, 
    password: string, 
    displayName?: string
  ): Promise<User> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }

      lastKnownIdToken = await result.user.getIdToken(true);
      
      return this.mapFirebaseUserToUser(result.user);
    } catch (error: any) {
      throw this.buildFirebaseError(error);
    }
  }

  async signInWithGoogle(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      lastKnownIdToken = await result.user.getIdToken(true);
      return this.mapFirebaseUserToUser(result.user);
    } catch (error: any) {
      if (error.code === 'auth/cancelled-popup-request' || 
          error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled');
      }
      throw new Error(this.mapFirebaseError(error.code));
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      lastKnownIdToken = null;
    } catch (error: any) {
      throw new Error('Failed to sign out');
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(this.mapFirebaseError(error.code));
    }
  }

  async updateUserProfile(updates: {
    displayName?: string;
    photoURL?: string;
  }): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      await updateProfile(user, updates);
    } catch (error: any) {
      throw new Error('Failed to update profile');
    }
  }

  async sendVerificationEmail(redirectUrl?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      if (redirectUrl) {
        await sendEmailVerification(user, {
          url: redirectUrl,
          handleCodeInApp: false,
        });
      } else {
        await sendEmailVerification(user);
      }
    } catch (error: any) {
      throw this.buildFirebaseError(error);
    }
  }

  async reloadCurrentUser(): Promise<User | null> {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    try {
      await user.reload();
      return this.mapFirebaseUserToUser(user);
    } catch (error: any) {
      throw this.buildFirebaseError(error);
    }
  }

  async applyEmailVerificationCode(actionCode: string): Promise<User | null> {
    const normalizedCode = String(actionCode || '').trim();
    if (!normalizedCode) {
      throw new Error('Verification link is missing a code.');
    }

    try {
      await applyActionCode(auth, normalizedCode);
      return await this.reloadCurrentUser();
    } catch (error: any) {
      throw this.buildFirebaseError(error);
    }
  }

  async uploadFile(file: File, path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error: any) {
      throw new Error('Failed to upload file');
    }
  }

  onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        void firebaseUser.getIdToken().then((token) => {
          lastKnownIdToken = token;
        }).catch(() => {
          // Ignore token refresh errors here; request interceptors will retry on demand.
        });
        callback(this.mapFirebaseUserToUser(firebaseUser));
      } else {
        lastKnownIdToken = null;
        callback(null);
      }
    });
  }

  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  private mapFirebaseUserToUser(firebaseUser: FirebaseUser): User {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      emailVerified: firebaseUser.emailVerified,
    };
  }

  private mapFirebaseError(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/invalid-action-code':
        return 'This verification link is invalid, expired, or has already been used.';
      case 'auth/expired-action-code':
        return 'This verification link has expired. Please request a new verification email.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/requires-recent-login':
        return 'Please sign in again to perform this action.';
      case 'auth/configuration-not-found':
        return 'The demo Firebase project is missing password sign-in configuration; please use your own account or contact the team.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  private buildFirebaseError(error: any): Error {
    const reportedCode = error?.code ?? 'auth/unknown';
    const mappedMessage = this.mapFirebaseError(reportedCode.toLowerCase());
    const fallback = error?.message ?? 'An error occurred. Please try again.';
    const message = mappedMessage ?? fallback;
    const enhancedError = new Error(message);
    (enhancedError as any).code = reportedCode;
    return enhancedError;
  }
}

export const firebaseService = new FirebaseService();
export { app };
export default firebaseService;
